"use client";

import { useAuiState } from "@assistant-ui/react";
import { useMemo, useState } from "react";

type ToolCardStatus = { readonly type: string };

type ToolCardGatingResult<T> =
  | { state: "hidden" }
  | { state: "loading" }
  | { state: "ready"; args: T; onSubmitted: () => void };

/**
 * Centralises the gating logic shared by every tool-card wrapper (`AskCard`, `AskOpenCard`).
 *
 * The hook decides whether the card should hide (already submitted / historical / complete),
 * show a loading placeholder, or render the interactive form with parsed args.
 *
 * @param toolCallId  – from the tool-call part props
 * @param argsText    – raw JSON string of tool arguments
 * @param status      – tool-call part status (`requires-action` | `complete` | …)
 * @param isValid     – optional extra check on parsed args (e.g. non-empty questions array)
 */
export function useToolCardGating<T>(
  toolCallId: string,
  argsText: string,
  status: ToolCardStatus,
  isValid?: (args: T) => boolean,
): ToolCardGatingResult<T> {
  const [submitted, setSubmitted] = useState(false);

  const historicalResult = useAuiState(({ message }) => {
    const part = (message.parts as any[]).find(
      (p: any) => p.type === "tool-call" && p.toolCallId === toolCallId,
    );
    return (part as any)?.result as string | undefined;
  });

  const args = useMemo<T | null>(() => {
    if (!argsText) return null;
    try {
      return JSON.parse(argsText) as T;
    } catch {
      return null;
    }
  }, [argsText]);

  if (submitted) return { state: "hidden" };
  if (historicalResult && historicalResult.length > 0) return { state: "hidden" };
  if (status.type === "complete") return { state: "hidden" };
  if (status.type === "incomplete") return { state: "hidden" };

  // Accept both "requires-action" (legacy: stream ended, new turn needed) and
  // "running" (hybrid wait: stream stays open, TCS waiting for answer). The
  // hybrid-wait path keeps messageStatus as "running" so thread.isRunning stays
  // true and the composer remains disabled — no flash between onSubmitted() and
  // the data-tool-call-result-inline SSE event.
  if ((status.type === "requires-action" || status.type === "running") && args && (isValid ? isValid(args) : true)) {
    return { state: "ready", args, onSubmitted: () => setSubmitted(true) };
  }

  return { state: "loading" };
}
